import type { PropsWithChildren, ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useUser } from "@sdeapps/react-core";
import type Article from "models/Article";
import { enqueueSnackbar } from "notistack";
import articlesService from "services/articlesService";
import ApplicationRoles from "constants/ApplicationRoles";
import ToastMessages from "constants/ToastMessages";
import { usePersistentValue } from "hooks";
import { localStorageKeys } from "config/app-config";
import AllMetiersCode from "constants/AllMetiersCode";
import type { Metier } from "models";
import metiersService from "services/metiersService";
import { sortMetier } from "utils/metierUtil";

export interface ICatalogueData {
  updateCatalog: () => Promise<void>;
  articles: Array<Article>;
  isArticlesLoading: boolean;
  selectedMetierId: string;
  setSelectedMetierId: (_: string) => void;
  metiers: Array<Metier>;
  isMetiersLoading: boolean;
}

const data: ICatalogueData = {
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  updateCatalog: async () => {},
  articles: [],
  isArticlesLoading: false,
  selectedMetierId: "",
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setSelectedMetierId: () => {},
  metiers: [],
  isMetiersLoading: false,
};

const CatalogueContext = createContext(data);

interface CatalogueProviderProps extends PropsWithChildren {}

export function CatalogueProvider({ children }: Readonly<CatalogueProviderProps>): ReactNode {
  const { roles } = useUser();

  const [articles, setArticles] = useState<Array<Article>>([]);
  const [selectedMetierId, setSelectedMetierId] = usePersistentValue<string>(
    localStorageKeys.selectedMetierId,
    AllMetiersCode
  );
  // TODO remplacer par un useErrorHandler()
  const [isArticlesLoading, setIsArticlesLoading] = useState<boolean>(true);

  const updateCatalog = useCallback(async (): Promise<void> => {
    try {
      setIsArticlesLoading(true);
      const allArticles = await articlesService.getAll();

      if (!roles?.includes(ApplicationRoles.SSTMANAGER)) {
        // Filtrer les articles non disponibles pour les non-admins
        const availableArticles = allArticles.filter((article) => article.isActive);
        setArticles(availableArticles);
      } else {
        setArticles(
          allArticles.toSorted((a, b) => {
            if (a.isActive && !b.isActive) return -1;
            if (!a.isActive && b.isActive) return 1;
            return 0;
          })
        );
      }
    } catch {
      setArticles([]);
      enqueueSnackbar(ToastMessages.ERROR_RETRY, { variant: "error" });
    } finally {
      setIsArticlesLoading(false);
    }
  }, [roles]);

  useEffect(() => {
    void updateCatalog();
  }, [updateCatalog]);

  const [metiers, setMetiers] = useState<Array<Metier>>([]);
  // TODO remplacer par un useErrorHandler()
  const [isMetiersLoading, setIsMetiersLoading] = useState<boolean>(true);
  const updateMetiers = useCallback(async (): Promise<void> => {
    try {
      setIsMetiersLoading(true);
      const _metiers = await metiersService.getAll();
      setMetiers(sortMetier(_metiers));
    } catch (error) {
      setMetiers([]);
      enqueueSnackbar(ToastMessages.ERROR_RETRY, { variant: "error" });
    } finally {
      setIsMetiersLoading(false);
    }
  }, []);

  useEffect(() => {
    void updateMetiers();
  }, [updateMetiers]);

  const data = useMemo(
    () => ({
      updateCatalog,
      articles,
      isArticlesLoading,
      selectedMetierId,
      setSelectedMetierId,
      metiers,
      isMetiersLoading,
    }),
    [
      updateCatalog,
      articles,
      isArticlesLoading,
      selectedMetierId,
      setSelectedMetierId,
      metiers,
      isMetiersLoading,
    ]
  );

  return <CatalogueContext.Provider value={data}>{children}</CatalogueContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useCatalogue(): ICatalogueData {
  const context = useContext(CatalogueContext);

  if (context == null) {
    throw new Error("useCatalogue must be used within a CatalogueProvider");
  }

  return context;
}
